import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Clock, Trash2, Pencil, Plus, X, Check } from 'lucide-react';

const GREEN = '#0F6E56';
const MAX_DESCRIPTION = 500;

function formatDuration(minutes) {
  if (!minutes && minutes !== 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h}h ${m}min`;
  if (h) return `${h}h`;
  return `${m}min`;
}

function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDateBr(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * Modal to view/add/edit/delete time entries for a single task.
 * Props:
 *  - open, onOpenChange
 *  - task: { task_id, name }
 *  - currentUser: { user_id, role }
 *  - onChange (optional): called after list mutates so parent can refresh summary
 */
export default function TimeEntryModal({ open, onOpenChange, task, currentUser, onChange }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form state
  const [editingId, setEditingId] = useState(null);
  const [entryDate, setEntryDate] = useState(todayIso());
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const isAdmin = (currentUser?.role || '').toUpperCase() === 'ADMIN';

  const totalMinutes = useMemo(
    () => entries.reduce((sum, e) => sum + (e.duration_minutes || 0), 0),
    [entries],
  );

  const load = async () => {
    if (!task?.task_id) return;
    setLoading(true);
    try {
      const { data } = await api.get('/time-entries', { params: { task_id: task.task_id } });
      setEntries(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar registros');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && task?.task_id) {
      load();
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.task_id]);

  const resetForm = () => {
    setEditingId(null);
    setEntryDate(todayIso());
    setHours('');
    setMinutes('');
    setDescription('');
  };

  const startEdit = (e) => {
    setEditingId(e.entry_id);
    setEntryDate(e.entry_date);
    const h = Math.floor((e.duration_minutes || 0) / 60);
    const m = (e.duration_minutes || 0) % 60;
    setHours(h > 0 ? String(h) : '');
    setMinutes(m > 0 ? String(m) : '');
    setDescription(e.description || '');
  };

  const parsedDuration = () => {
    const h = parseInt(hours || '0', 10) || 0;
    const m = parseInt(minutes || '0', 10) || 0;
    return h * 60 + m;
  };

  const validate = () => {
    if (!entryDate) return 'Informe a data.';
    const total = parsedDuration();
    if (total <= 0) return 'Duração deve ser maior que zero.';
    if (total > 24 * 60) return 'Duração máxima por lançamento é 24 horas.';
    if ((description || '').length > MAX_DESCRIPTION) {
      return `Descrição excede ${MAX_DESCRIPTION} caracteres.`;
    }
    return null;
  };

  const handleSubmit = async (ev) => {
    ev?.preventDefault?.();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    const payload = {
      entry_date: entryDate,
      duration_minutes: parsedDuration(),
      description: (description || '').trim() || null,
    };
    try {
      if (editingId) {
        const { data } = await api.put(`/time-entries/${editingId}`, payload);
        setEntries((prev) => prev.map((e) => (e.entry_id === editingId ? data : e)));
        toast.success('Registro atualizado');
      } else {
        const { data } = await api.post('/time-entries', {
          ...payload,
          task_id: task.task_id,
        });
        setEntries((prev) => [data, ...prev]);
        toast.success('Horas registradas');
      }
      resetForm();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Excluir este registro?')) return;
    try {
      await api.delete(`/time-entries/${entryId}`);
      setEntries((prev) => prev.filter((e) => e.entry_id !== entryId));
      if (editingId === entryId) resetForm();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const canModify = (entry) =>
    isAdmin || entry.user_id === currentUser?.user_id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="time-entry-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" style={{ color: GREEN }} />
            Registro de horas — {task?.name || 'Tarefa'}
          </DialogTitle>
          <DialogDescription>
            Total registrado: <span className="font-medium">{formatDuration(totalMinutes)}</span>
            {' '}em {entries.length} lançamento(s).
          </DialogDescription>
        </DialogHeader>

        {/* Form */}
        <form onSubmit={handleSubmit} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
            {editingId ? (
              <><Pencil className="w-4 h-4" /> Editando lançamento</>
            ) : (
              <><Plus className="w-4 h-4" /> Novo lançamento</>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <Label htmlFor="te-date">Data</Label>
              <Input
                id="te-date"
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                data-testid="time-entry-date"
              />
            </div>
            <div>
              <Label htmlFor="te-hours">Horas</Label>
              <Input
                id="te-hours"
                type="number"
                min="0"
                max="24"
                step="1"
                placeholder="0"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                data-testid="time-entry-hours"
              />
            </div>
            <div>
              <Label htmlFor="te-minutes">Minutos</Label>
              <Input
                id="te-minutes"
                type="number"
                min="0"
                max="59"
                step="1"
                placeholder="0"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                data-testid="time-entry-minutes"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="te-desc">Descrição (opcional)</Label>
            <Textarea
              id="te-desc"
              rows={2}
              placeholder="O que foi feito?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={MAX_DESCRIPTION}
              data-testid="time-entry-description"
            />
            <p className="text-[10px] text-gray-400 mt-0.5">
              {(description || '').length}/{MAX_DESCRIPTION}
            </p>
          </div>

          <div className="flex items-center gap-2 justify-end">
            {editingId && (
              <Button type="button" variant="outline" size="sm" onClick={resetForm}>
                <X className="w-3.5 h-3.5 mr-1" /> Cancelar
              </Button>
            )}
            <Button
              type="submit"
              size="sm"
              disabled={saving}
              style={{ backgroundColor: GREEN }}
              className="text-white hover:opacity-90"
              data-testid="time-entry-submit"
            >
              <Check className="w-3.5 h-3.5 mr-1" />
              {saving ? 'Salvando...' : editingId ? 'Salvar alterações' : 'Registrar'}
            </Button>
          </div>
        </form>

        {/* Entries list */}
        <div className="border-t border-gray-200 pt-3 max-h-72 overflow-y-auto">
          {loading ? (
            <p className="text-xs text-gray-500 py-3">Carregando registros...</p>
          ) : entries.length === 0 ? (
            <p className="text-xs text-gray-500 italic py-3">
              Nenhum lançamento ainda. Registre suas horas acima.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100" data-testid="time-entries-list">
              {entries.map((e) => (
                <li key={e.entry_id} className="py-2 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-gray-900 flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{formatDateBr(e.entry_date)}</span>
                      <span className="text-gray-500">·</span>
                      <span className="font-semibold" style={{ color: GREEN }}>
                        {formatDuration(e.duration_minutes)}
                      </span>
                      {isAdmin && (
                        <span className="text-xs text-gray-500">· {e.user_name}</span>
                      )}
                    </div>
                    {e.description && (
                      <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap break-words">
                        {e.description}
                      </p>
                    )}
                  </div>
                  {canModify(e) && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => startEdit(e)}
                        className="p-1 text-gray-400 hover:text-emerald-700"
                        title="Editar"
                        data-testid={`time-entry-edit-${e.entry_id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(e.entry_id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Excluir"
                        data-testid={`time-entry-delete-${e.entry_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
